import React, { useState, useEffect, useContext } from 'react';
import useApi from '../../../utils/api-manager/Helper/useApi';
import { ToastContainer } from 'react-toastify';
import AuthContext from '../../../utils/secure-route/AuthContext';
import PaginatedSearchSelect from '../../common/ReusableComponent/PaginatedSearchSelect/PaginatedSearchSelect';
import loader from '../../../assets/Image/Loader2.gif';
import './CameraMedia.css';

const TABS = ['Snapshots', 'Recordings'];
const PAGE_SIZE = 10;

function CameraMedia() {
  const { Get, getAPI, getHost, GetPaginatedData } = useApi();
  const [tab, setTab] = useState('Snapshots');
  const [cameraObj, setCameraObj] = useState(null);
  const [cameraId, setCameraId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [clock, setClock] = useState('');

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  useEffect(() => {
    const tick = () => setClock(new Date().toLocaleTimeString('en-GB', { hour12: false }));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => { setPage(1); }, [tab, cameraId, dateFrom, dateTo]);
  useEffect(() => { fetchData(); }, [tab, cameraId, dateFrom, dateTo, page]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const api = tab === 'Snapshots' ? 'snapshotData' : 'recordingData';
      const params = new URLSearchParams();
      if (cameraId) params.append('camera_id', cameraId);
      if (page > 1) params.append('page', page);
      if (tab === 'Recordings') {
        if (dateFrom) params.append('date_from', dateFrom);
        if (dateTo) params.append('date_to', dateTo);
      }
      const res = await Get(`${getAPI(api)}?${params.toString()}`);
      if (res?.results) { setData(res.results); setTotalCount(res.count || 0); }
      else { setData(Array.isArray(res) ? res : []); setTotalCount(Array.isArray(res) ? res.length : 0); }
    } catch { setData([]); setTotalCount(0); }
    finally { setLoading(false); }
  };

  const dateStr = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase();

  return (
    <>
      <div className="monitor-media-wrap responsive-box">
        <div className="monitor-media-content">
          <div className="monitor-media-topbar">
            <div className="monitor-media-brand">
              <div className="monitor-media-brand-mark">
                <svg className="monitor-media-brand-svg" viewBox="0 0 24 24">
                  <path d="M20 5h-3.17L15 3H9L7.17 5H4a2 2 0 00-2 2v12a2 2 0 002 2h16a2 2 0 002-2V7a2 2 0 00-2-2zm-10 11l-5-5 1.41-1.41L10 13.17l7.59-7.59L19 7l-9 9z" />
                </svg>
              </div>
              <div>
                <div className="monitor-media-brand-name">Camera Media</div>
                <div className="monitor-media-brand-sub">▸ MEDIA ARCHIVE SYSTEM</div>
              </div>
            </div>
            <div className="monitor-media-topbar-right">
              <div className="monitor-media-sys-pill">
                <span className="monitor-media-sys-dot" />
                SYS ONLINE
              </div>
              <div className="monitor-media-clock">
                <div className="monitor-media-clock-time">{clock}</div>
                <div className="monitor-media-clock-date">{dateStr}</div>
              </div>
            </div>
          </div>

          <div className="monitor-media-toolbar">
            <div className="monitor-media-tabs">
              {TABS.map(t => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  className={`monitor-media-tab ${tab === t ? 'active' : 'inactive'}`}
                >
                  {t === 'Snapshots' ? '📷  Snapshots' : '🎬  Recordings'}
                </button>
              ))}
            </div>

            <div className="monitor-media-divider" />

            <div className="monitor-media-select-wrap">
              <PaginatedSearchSelect
                api="cameraData" GetPaginatedData={GetPaginatedData} method="GET"
                value={cameraObj}
                onSelect={s => { setCameraObj(s); setCameraId(s?.id || ''); }}
                getOptionLabel={o => o?.name || ''} placeholder="All Cameras" pageSize={10}
              />
            </div>

            {tab === 'Recordings' && (
              <div className="monitor-media-date-wrap">
                <div className="monitor-media-date-field">
                  <span className="monitor-media-date-label">From</span>
                  <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="monitor-media-date-input" />
                </div>
                <span className="monitor-media-date-arrow">→</span>
                <div className="monitor-media-date-field">
                  <span className="monitor-media-date-label">To</span>
                  <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="monitor-media-date-input" />
                </div>
              </div>
            )}

            {!loading && totalCount > 0 && (
              <div className="monitor-media-count-badge">{totalCount} {tab.toLowerCase()}</div>
            )}
          </div>
          {loading ? (
            <div className="monitor-media-state-box">
              <img src={loader} alt="Loading..." style={{ width: '80px' }} />
            </div>
          ) : data.length === 0 ? (
            <div className="monitor-media-state-box">
              <div className="monitor-media-empty-icon">{tab === 'Snapshots' ? '📷' : '🎬'}</div>
              <div className="monitor-media-empty-title">No {tab.toLowerCase()} found</div>
              <div className="monitor-media-empty-hint">
                {tab === 'Snapshots'
                  ? 'No snapshots captured yet. Make sure cameras are online and snapshot scheduling is enabled.'
                  : 'No recordings found for the selected period. Try a different date range or camera.'}
              </div>
            </div>
          ) : tab === 'Snapshots' ? (
            <SnapshotGrid data={data} getHost={getHost} />
          ) : (
            <RecordingGrid data={data} getHost={getHost} />
          )}
          {totalPages > 1 && (
            <div className="monitor-media-pagination">
              {buildPages(totalPages, page).map((p, i) =>
                p === '...' ? (
                  <span key={`d${i}`} className="monitor-media-page-dots">···</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`monitor-media-page-btn${p === page ? ' active' : ''}`}
                  >{p}</button>
                )
              )}
            </div>
          )}

        </div>
      </div>

      <ToastContainer />
    </>
  );
}

function buildPages(total, current) {
  return Array.from({ length: total }, (_, i) => i + 1)
    .filter(p => p === 1 || p === total || Math.abs(p - current) <= 1)
    .reduce((acc, p, idx, arr) => {
      if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
      acc.push(p);
      return acc;
    }, []);
}

function SnapshotGrid({ data, getHost }) {
  const [preview, setPreview] = useState(null);
  return (
    <>
      <div className="monitor-media-snap-grid">
        {data.map((item, i) => {
          const rel = item.url || item.image_url || item.snapshot_url || '';
          const url = rel.startsWith('/') ? `${getHost()}${rel}` : `${getHost()}/${rel}`;
          return (
            <div key={item.id || i} className="monitor-media-snap-card" onClick={() => setPreview(item)}>
              <div className="monitor-media-snap-img-wrap">
                <img src={url} alt="snapshot" className="monitor-media-snap-img" onError={e => { e.target.style.display = 'none'; }} />
                <div className="monitor-media-snap-overlay" />
                <div className="monitor-media-snap-badge-top">SNAP</div>
              </div>
              <div className="monitor-media-snap-body">
                <div className="monitor-media-snap-name">{item.camera_name || '—'}</div>
                <div className="monitor-media-snap-time">
                  <span className="monitor-media-snap-time-dot">◷</span>
                  {item.taken_at ? new Date(item.taken_at).toLocaleString() : '—'}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {preview && (
        <div className="monitor-media-lightbox" onClick={() => setPreview(null)}>
          <div className="monitor-media-lightbox-inner" onClick={e => e.stopPropagation()}>
            {(() => {
              const rel = preview.url || preview.image_url || preview.snapshot_url || '';
              const fullUrl = rel.startsWith('/') ? `${getHost()}${rel}` : `${getHost()}/${rel}`;
              return <img src={fullUrl} alt="preview" style={{ maxWidth: '80vw', maxHeight: '76vh', display: 'block', objectFit: 'contain' }} />;
            })()}
            <div className="monitor-media-lightbox-footer">
              <div>
                <div className="monitor-media-lightbox-name">{preview.camera_name}</div>
                <div className="monitor-media-lightbox-time">{preview.taken_at ? new Date(preview.taken_at).toLocaleString() : ''}</div>
              </div>
              <button className="monitor-media-lightbox-close" onClick={() => setPreview(null)}>✕ Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function RecordingGrid({ data, getHost }) {
  const [selected, setSelected] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [fetching, setFetching] = useState(false);
  const { authToken } = useContext(AuthContext);

  const getFileUrl = item => {
    const rel = item?.file_url || item?.video_url || item?.recording_url || item?.url;
    if (!rel) return null;
    if (rel.startsWith('http')) return rel;
    return rel.startsWith('/') ? `${getHost()}${rel}` : `${getHost()}/${rel}`;
  };

  const handleSelect = item => {
    if (selected?.id === item.id) { setSelected(null); setBlobUrl(null); return; }
    setSelected(item);
  };

  useEffect(() => {
    if (!selected) { setBlobUrl(null); return; }
    const url = getFileUrl(selected);
    if (!url) return;
    let objectUrl = null;
    setFetching(true); setBlobUrl(null);
    fetch(url, { headers: { Authorization: `Bearer ${authToken?.access}` } })
      .then(r => r.blob())
      .then(blob => { objectUrl = URL.createObjectURL(blob); setBlobUrl(objectUrl); })
      .catch(e => console.error('[Video fetch error]', e))
      .finally(() => setFetching(false));
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [selected]);

  return (
    <div>
      {selected && (
        <div className="monitor-media-player">
          <div className="monitor-media-player-hdr">
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <div className="monitor-media-player-icon">▶</div>
              <div>
                <div className="monitor-media-player-title">{selected.camera_name}</div>
                <div className="monitor-media-player-sub">
                  {selected.start_time ? new Date(selected.start_time).toLocaleString() : ''}
                  {selected.end_time ? ` → ${new Date(selected.end_time).toLocaleString()}` : ''}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              {selected.file_size_mb && (
                <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', background: 'rgba(255,255,255,0.08)', padding: '5px 12px', borderRadius: '20px', fontWeight: '600', fontFamily: 'Share Tech Mono, monospace' }}>
                  {selected.file_size_mb} MB
                </span>
              )}
              <button className="monitor-media-player-close" onClick={() => { setSelected(null); setBlobUrl(null); }}>✕ Close</button>
            </div>
          </div>
          <div className="monitor-media-player-video">
            {fetching ? (
              <div style={{ height: '320px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#000' }}>
                <img src={loader} alt="Loading..." style={{ width: '80px' }} />
              </div>
            ) : blobUrl ? (
              <video key={blobUrl} src={blobUrl} controls autoPlay style={{ width: '100%', maxHeight: '500px', display: 'block' }} onError={e => console.error('[Video error]', e)} />
            ) : (
              <div style={{ height: '200px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px', background: '#000', color: '#475569' }}>
                <span style={{ fontSize: '28px' }}>⚠️</span>
                <span style={{ fontSize: '12px', fontFamily: 'Share Tech Mono, monospace', letterSpacing: '0.08em' }}>NO VIDEO FILE AVAILABLE</span>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="monitor-media-rec-grid">
        {data.map((item, i) => {
          const isActive = selected?.id === item.id;
          const duration = item.duration || (item.start_time && item.end_time ? (() => {
            const diff = (new Date(item.end_time) - new Date(item.start_time)) / 1000;
            const h = Math.floor(diff / 3600), m = Math.floor((diff % 3600) / 60), s = Math.floor(diff % 60);
            return h > 0 ? `${h}h ${m}m` : `${m}m ${s}s`;
          })() : null);

          return (
            <div key={item.id || i} className={`monitor-media-rec-card${isActive ? ' active' : ''}`} onClick={() => handleSelect(item)}>
              <div className="monitor-media-rec-thumb">
                <div className="monitor-media-rec-thumb-dots" />
                <div className="monitor-media-rec-play-btn" style={{ background: isActive ? '#fbbf24' : 'rgba(255,255,255,0.08)', boxShadow: isActive ? '0 6px 20px rgba(251,191,36,0.5)' : 'none' }}>
                  {isActive ? '▶' : '🎬'}
                </div>
                {isActive && <div className="monitor-media-rec-now-badge">▶ NOW PLAYING</div>}
                {duration && <div className="monitor-media-rec-dur-badge">{duration}</div>}
              </div>
              <div className="monitor-media-rec-body">
                <div className="monitor-media-rec-name">{item.camera_name || `Recording ${i + 1}`}</div>
                <div className="monitor-media-rec-time">
                  <span style={{ color: '#d97706', fontSize: '12px' }}>◷</span>
                  {item.start_time ? new Date(item.start_time).toLocaleString() : '—'}
                </div>
                {item.file_size_mb && (
                  <div className="monitor-media-rec-size">
                    <span className="monitor-media-rec-size-badge">{item.file_size_mb} MB</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default CameraMedia;
